import React, { useEffect, useState } from 'react';
import { 
  collection, 
  onSnapshot, 
  query, 
  where, 
  doc, 
  deleteDoc, 
  updateDoc,
  setDoc 
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';
import { AddKidForm, AddRoutineForm, AddAppointmentForm } from './AddKid.js';


const formatTime = (timeStr) => {
  if (!timeStr) return '';
  const [hour, min] = timeStr.split(':');
  const h = parseInt(hour);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const displayHour = h % 12 || 12; // Converts "00" to "12" and "13" to "1"
  return `${displayHour}:${min} ${ampm}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  // Split the YYYY-MM-DD string to avoid timezone "shift" issues
  const [year, month, day] = dateStr.split('-');
  const date = new Date(year, month - 1, day);
  
  return date.toLocaleDateString('en-US', { 
    weekday: 'short', 
    month: 'short', 
    day: 'numeric' 
  });
};

const calculateAge = (birthdate) => {
  if (!birthdate) return '';
  const today = new Date();
  const birthDate = new Date(birthdate);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  
  // Adjust if the birthday hasn't happened yet this year
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

const KidSchedule = () => {
  const [kids, setKids] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const [editingKidId, setEditingKidId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [kidOrder, setKidOrder] = useState([]);

  useEffect(() => {
    if (!user) return;

    // Define "Today" in YYYY-MM-DD format to match your database
    const d = new Date();
    const today = d.toISOString().split('T')[0];

    // 1. Listen to the main 'kids' collection (Keep as is)
    const qKids = query(collection(db, 'kids'), where('userId', '==', user.uid));
    const unsubKids = onSnapshot(qKids, (snapshot) => {
      const kidList = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
        appointments: [], // Initial empty state
        routines: []      // Initial empty state
      }));
      setKids(kidList);
      setLoading(false);
    });

    // 2. UPDATED: Listen to the GLOBAL 'calendar' collection with a date filter
    const qCal = query(
      collection(db, 'calendar'), 
      where('userId', '==', user.uid),
      where('date', '>=', today) // <--- THIS IS THE KEY FIX
    );

    const unsubCalendar = onSnapshot(qCal, (snap) => {
      const allEvents = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      setKids(currentKids => currentKids.map(kid => ({
        ...kid,
        // Now this filtered list only contains current/future appointments
        appointments: allEvents
          .filter(e => e.kidId === kid.id)
          .sort((a, b) => a.date.localeCompare(b.date)) // Optional: Sort by date too
      })));
    });

    // 3. Keep routines separate and clean
    const routineUnsubs = [];
    // Use a temporary listener to find all kid IDs once
    const unsubRoutineTrigger = onSnapshot(qKids, (snapshot) => {
      // Clean up previous sub-listeners to prevent memory leaks
      routineUnsubs.forEach(unsub => unsub());
      
      snapshot.docs.forEach(kidDoc => {
        const u = onSnapshot(collection(db, 'kids', kidDoc.id, 'routines'), (routSnap) => {
          const routineData = routSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          
          setKids(currentKids => currentKids.map(k => 
            k.id === kidDoc.id ? { ...k, routines: routineData } : k
          ));
        });
        routineUnsubs.push(u);
      });
    });

    return () => {
      unsubKids();
      unsubCalendar();
      unsubRoutineTrigger();
      routineUnsubs.forEach(u => u());
    };
  }, [user]);

  //Initialize kids' turns
  const initializeRotation = async () => {
    if (kids.length === 0) {
      alert("Add some kids to your hub first!");
      return;
    }

    // Grab names from your existing 'kids' state
    const names = kids.map(k => k.name);
    
    try {
      const turnDoc = doc(db, `users/${user.uid}/settings`, 'kidTurnData');
      await setDoc(turnDoc, { 
        kidOrder: names 
      }, { merge: true });
      // The onSnapshot listener will pick this up and show the banner automatically
    } catch (err) {
      console.error("Error initializing rotation:", err);
    }
  };
  
  // Listen for global 'kid turn' order
  useEffect(() => {
    if (!user) return;
    const turnDoc = doc(db, `users/${user.uid}/settings`, 'kidTurnData');
    return onSnapshot(turnDoc, (snapshot) => {
      if (snapshot.exists()) {
        setKidOrder(snapshot.data().kidOrder || []);
      } else {
        // Optional: Initialize order from the 'kids' state if empty
        // but let's keep it manual/controlled for now
      }
    });
  }, [user]);

  //Formula for determing kids' turns
  const getRotationData = (names) => {
    if (!names || names.length === 0) return { leader: '---', onDeck: '---' };

    // 1. Get the local date string (e.g., "2026-04-28")
    // Using 'sv-SE' locale is a trick to get YYYY-MM-DD format easily
    const localDateStr = new Date().toLocaleDateString('sv-SE');
    
    // 2. Create a "Deterministic Seed" by counting days since a fixed local date
    const startDate = new Date('2024-01-01'); // A fixed Monday in the past
    const todayDate = new Date(localDateStr);
    
    // 3. Calculate full days between them
    const diffTime = Math.abs(todayDate - startDate);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    const leaderIndex = diffDays % names.length;
    const onDeckIndex = (diffDays + 1) % names.length;

    return {
      leader: names[leaderIndex],
      onDeck: names[onDeckIndex]
    };
  };

  //Manually swap kid turns, if needed
  const handleManualRotate = async () => {
    if (kidOrder.length < 2) return;

    // 1. Create a new array based on the current order
    const newOrder = [...kidOrder];
    
    // 2. Move the first kid to the back
    const shiftedKid = newOrder.shift();
    newOrder.push(shiftedKid);

    // 3. Save the new "Master Sequence" to Firestore
    const turnDoc = doc(db, `users/${user.uid}/settings`, 'kidTurnData');
    await updateDoc(turnDoc, {
      kidOrder: newOrder
    });
  };

  const startEditing = (kid) => {
    setEditingKidId(kid.id);
    setEditFormData({ 
      name: kid.name, 
      birthdate: kid.birthdate, 
      clothesSize: kid.clothesSize, 
      shoeSize: kid.shoeSize 
    });
  };

  const saveEdit = async (id) => {
    const kidRef = doc(db, 'kids', id);
    await updateDoc(kidRef, editFormData);
    setEditingKidId(null);
  };

  const handleDeleteKid = async (id) => {
    if (window.confirm("Are you sure? This deletes the profile and all scheduled items.")) {
      await deleteDoc(doc(db, 'kids', id));
    }
  };

  const deleteAppointment = async (appointmentId) => {
    await deleteDoc(doc(db, 'calendar', appointmentId));
  };

  const deleteRoutine = async (kidId, routineId) => {
    await deleteDoc(doc(db, 'kids', kidId, 'routines', routineId));
  };

  if (loading) return <div className="p-10 text-center text-gray-500">Loading family schedules...</div>;

  const { leader, onDeck } = getRotationData(kidOrder);

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Header Area */}
      <header className="flex justify-between items-center mb-8 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <div>
          <h2 className="text-3xl font-bold text-gray-800 tracking-tight">Kids' Hub</h2>
          <p className="text-gray-400 font-bold text-[10px] md:text-xs uppercase tracking-widest mt-1">
            Schedules, sizes, and snacks.
          </p>
        </div>
        <AddKidForm />
      </header>

      {/* Turn Tracker Banner */}
      {kidOrder.length > 0 ? (
        <div className="mb-8 bg-gradient-to-r from-indigo-600 to-blue-500 rounded-3xl p-1 shadow-lg overflow-hidden">
          <div className="bg-white/10 backdrop-blur-md p-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="bg-yellow-400 p-3 rounded-2xl shadow-inner rotate-3">
                <span className="text-2xl">👑</span>
              </div>
              <div>
                <p className="text-indigo-100 text-[10px] font-black uppercase tracking-widest">Today's First Turn</p>
                <h3 className="text-white text-3xl font-black tracking-tight">{leader}</h3>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              <div className="hidden md:block text-right">
                <p className="text-indigo-100 text-[10px] font-black uppercase tracking-widest">On Deck</p>
                <p className="text-white font-bold opacity-80">{onDeck}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <p className="text-indigo-100 text-[10px] font-black uppercase tracking-widest">Auto-Rotating</p>
                <button 
                  className="bg-white/20 px-4 py-2 rounded-xl text-white text-[10px] font-black uppercase tracking-widest border border-white/10"
                  onClick={handleManualRotate}
                  >
                    Override
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* INITIALIZE BUTTON: Only shows if kidOrder is empty */
        <div className="mb-8 p-6 bg-gray-50 border-2 border-dashed border-gray-200 rounded-3xl text-center">
          <p className="text-gray-500 mb-4 font-medium">Turn rotation not set up yet.</p>
          <button 
            onClick={initializeRotation}
            className="bg-indigo-600 text-white px-6 py-2 rounded-xl font-bold hover:bg-indigo-700 transition shadow-md"
          >
            Start Turn Rotation
          </button>
        </div>
      )}

      {/* Grid of Kid Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {kids.map((kid) => (
          <div key={kid.id} className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden flex flex-col transition-all hover:shadow-md">
            
            {/* Top Header Section */}
            <div className="bg-gradient-to-br from-green-50 to-blue-50 p-6 border-b border-gray-100">
              <div className="flex justify-between items-start">
                {editingKidId === kid.id ? (
                  /* EDIT MODE UI */
                  <div className="flex-1 space-y-3 mr-4">
                    <input 
                      className="block w-full p-2 border rounded-lg text-lg font-bold outline-none focus:ring-2 focus:ring-green-400"
                      value={editFormData.name}
                      onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <input type="date" className="p-2 border rounded-lg text-xs" value={editFormData.birthdate} onChange={(e) => setEditFormData({...editFormData, birthdate: e.target.value})} placeholder="Age" />
                      <input className="p-2 border rounded-lg text-xs" value={editFormData.clothesSize} onChange={(e) => setEditFormData({...editFormData, clothesSize: e.target.value})} placeholder="Clothes" />
                      <input className="p-2 border rounded-lg text-xs" value={editFormData.shoeSize} onChange={(e) => setEditFormData({...editFormData, shoeSize: e.target.value})} placeholder="Shoe" />
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button onClick={() => saveEdit(kid.id)} className="bg-green-600 text-white px-4 py-1.5 rounded-full text-xs font-bold hover:bg-green-700">Save Changes</button>
                      <button onClick={() => setEditingKidId(null)} className="bg-gray-200 text-gray-600 px-4 py-1.5 rounded-full text-xs font-bold hover:bg-gray-300">Cancel</button>
                    </div>
                  </div>
                ) : (
                  /* VIEW MODE UI */
                  <div className="flex-1">
                    <h3 className="text-2xl font-black text-gray-800">{kid.name}</h3>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <span className="bg-white/90 backdrop-blur-sm text-gray-600 text-[11px] px-3 py-1 rounded-full shadow-sm border border-gray-100 font-bold uppercase tracking-wider">🎂 {calculateAge(kid.birthdate)} Years</span>
                      <span className="bg-white/90 backdrop-blur-sm text-blue-600 text-[11px] px-3 py-1 rounded-full shadow-sm border border-gray-100 font-bold uppercase tracking-wider">👕 Size: {kid.clothesSize || '—'}</span>
                      <span className="bg-white/90 backdrop-blur-sm text-orange-600 text-[11px] px-3 py-1 rounded-full shadow-sm border border-gray-100 font-bold uppercase tracking-wider">👟 Shoe: {kid.shoeSize || '—'}</span>
                    </div>
                  </div>
                )}
                
                {/* Action Buttons Group */}
                <div className="flex gap-1 bg-white/40 p-1 rounded-full border border-white/50 backdrop-blur-sm">
                  <button onClick={() => startEditing(kid)} className="p-2 text-gray-400 hover:text-blue-500 transition-colors rounded-full hover:bg-white shadow-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  </button>
                  <button onClick={() => handleDeleteKid(kid.id)} className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-full hover:bg-white shadow-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Content Section (Two Columns) */}
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8 bg-white">
              {/* Weekly Activities Column */}
              <div className="flex flex-col h-full">
                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-4">Weekly Activities</h4>
                <div className="space-y-3 flex-grow">
                  {kid.routines?.filter(r => r.category === 'activity').map((act) => (
                    <div key={act.id} className="p-3 bg-blue-50/50 rounded-xl border border-blue-100 flex justify-between items-center group">
                      <div>
                        <p className="font-bold text-blue-900 text-sm leading-tight">{act.activity}</p>
                        <div className="flex gap-1 mt-1">
                          <span className="text-[10px] text-blue-600 font-bold mr-2">{formatTime(act.time)}</span>
                          {/* Display Day Bubbles */}
                          {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(day => (
                            <span 
                              key={day} 
                              className={`text-[8px] font-black ${act.days?.includes(day) ? 'text-blue-600' : 'text-gray-300'}`}
                            >
                              {day}
                            </span>
                          ))}
                        </div>
                      </div>
                      <button onClick={() => deleteRoutine(kid.id, act.id)} className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-full hover:bg-white shadow-sm">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                      </button>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-50">
                  <AddRoutineForm kidId={kid.id} category="activity" />
                </div>
              </div>

              {/* Appointments Column */}
              <div className="flex flex-col h-full">
                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-4">Appointments</h4>
                <div className="space-y-3 flex-grow">
                  {kid.appointments?.map((appt) => (
                    <div key={appt.id} className="p-3 bg-purple-50/50 rounded-xl border border-purple-100 flex justify-between items-center group">
                      <div>
                        <p className="font-bold text-purple-900 text-sm leading-tight">{appt.title}</p>
                        <p className="text-[11px] text-purple-500 font-semibold uppercase">{formatDate(appt.date)}</p>
                      </div>
                      <button onClick={() => deleteAppointment(appt.id)} className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-full hover:bg-white shadow-sm">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                      </button>
                  </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-50">
                  <AddAppointmentForm kidId={kid.id} kidName={kid.name} />
                </div>
              </div>

            {/* Schedule Row */}
              <div className="flex flex-col h-full">
                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-4">Schedule</h4>
                <div className="space-y-3 flex-grow">
                  {kid.routines?.filter(r => r.category === 'schedule').map((sched) => (
                    <div key={sched.id} className="p-3 bg-green-50/50 rounded-xl border border-green-100 flex justify-between items-center group">
                      <div>
                        <p className="font-bold text-green-900 text-sm leading-tight">{sched.activity}</p>
                        <div className="flex gap-1 mt-1">
                          <span className="text-[10px] text-green-600 font-bold mr-2">{formatTime(sched.time)}</span>
                          {/* Display Day Bubbles */}
                          {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(day => (
                            <span 
                              key={day} 
                              className={`text-[8px] font-black ${sched.days?.includes(day) ? 'text-green-600' : 'text-gray-300'}`}
                            >
                              {day}
                            </span>
                          ))}
                        </div>
                      </div>
                      <button onClick={() => deleteRoutine(kid.id, sched.id)} className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-full hover:bg-white shadow-sm">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                      </button>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-50">
                  <AddRoutineForm kidId={kid.id} category="schedule" />
                </div>
              </div>

            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default KidSchedule;